import type { EventLogEntry, EventType } from '@/lib/events-api';
import type { Tone } from './palette';

export type ActivityKind = 'dispatch' | 'task' | 'estop' | 'takeover';

export interface ActivityRow {
  id: number;
  kind: ActivityKind;
  actor: string;
  action: string;
  target: string | null;
  detail: string;
  time: Date;
  status: 'DONE' | 'IN PROGRESS';
  tone: Tone;
}

export interface Summary {
  dispatches: number;
  tasksStarted: number;
  recentStops: number;
  avgTravelSeconds: number | null;
  totalDistanceMeters: number | null;
}

export interface Destination {
  name: string;
  count: number;
}

const COMPLETED_TYPES: EventType[] = ['task_completed', 'dispatched_robot', 'started_driving', 'e_stop_pressed'];
void COMPLETED_TYPES;

function parseUtc(s: string): Date {
  return new Date(s + 'Z');
}

// The fleet operates in California, so the activity log and weekly summary render
// in Pacific regardless of where the dashboard is being viewed from. Backend
// stores UTC; we format/bucket against the Pacific calendar.
const PACIFIC_TZ = 'America/Los_Angeles';

/** Pacific calendar day (YYYY-MM-DD) for an instant. */
function pacificDateKey(d: Date): string {
  return d.toLocaleDateString('en-CA', { timeZone: PACIFIC_TZ });
}

/** Integer day-number of the Pacific civil date — for day-bucket arithmetic. */
function pacificDayNumber(d: Date): number {
  const [y, m, day] = pacificDateKey(d).split('-').map(Number);
  return Math.floor(Date.UTC(y, m - 1, day) / 86_400_000);
}

export function summarize(events: EventLogEntry[]): Summary {
  let dispatches = 0;
  let tasksStarted = 0;
  let recentStops = 0;
  let travelSum = 0;
  let travelCount = 0;
  let distanceSum = 0;
  let distanceCount = 0;

  for (const e of events) {
    const info = e.extra_info;
    switch (info.type) {
      case 'dispatched_robot':
        dispatches += 1;
        if (info.travel_time_seconds != null) {
          travelSum += info.travel_time_seconds;
          travelCount += 1;
        }
        if (info.distance_meters != null) {
          distanceSum += info.distance_meters;
          distanceCount += 1;
        }
        break;
      case 'started_task':
        tasksStarted += 1;
        break;
      case 'e_stop_pressed':
        recentStops += 1;
        break;
      default:
        break;
    }
  }

  return {
    dispatches,
    tasksStarted,
    recentStops,
    avgTravelSeconds: travelCount > 0 ? Math.round(travelSum / travelCount) : null,
    totalDistanceMeters: distanceCount > 0 ? distanceSum : null,
  };
}

export function topDestinations(events: EventLogEntry[], n = 3): Destination[] {
  const counts = new Map<string, number>();
  for (const e of events) {
    if (e.extra_info.type === 'dispatched_robot') {
      const dest = e.extra_info.destination;
      counts.set(dest, (counts.get(dest) ?? 0) + 1);
    }
  }
  return Array.from(counts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, n);
}

// Last N task_completed cycle times in chronological order.
export function cycleTimes(events: EventLogEntry[], n = 12): number[] {
  const completed: { time: Date; secs: number }[] = [];
  for (const e of events) {
    if (e.extra_info.type === 'task_completed') {
      completed.push({ time: parseUtc(e.created_at), secs: e.extra_info.cycle_time_seconds });
    }
  }
  completed.sort((a, b) => a.time.getTime() - b.time.getTime());
  return completed.slice(-n).map((c) => c.secs);
}

// Count completed tasks per day index for the given week (Mon-Sun), bucketed by
// Pacific calendar day. `weekStart` is a UTC-midnight anchor for the Pacific
// Monday (see startOfWeek), so its day-number lines up with pacificDayNumber.
export function byDayTaskCounts(events: EventLogEntry[], weekStart: Date): number[] {
  const buckets = [0, 0, 0, 0, 0, 0, 0];
  const startDay = Math.floor(weekStart.getTime() / 86_400_000);
  for (const e of events) {
    if (e.extra_info.type !== 'task_completed') continue;
    const dayIdx = pacificDayNumber(parseUtc(e.created_at)) - startDay;
    if (dayIdx >= 0 && dayIdx < 7) buckets[dayIdx] += 1;
  }
  return buckets;
}

function formatTravel(seconds: number | null | undefined): string | null {
  if (seconds == null) return null;
  if (seconds < 60) return `Travel time ${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `Travel time ${m}m ${s.toString().padStart(2, '0')}s` : `Travel time ${m}m`;
}

export function toActivityRows(events: EventLogEntry[]): ActivityRow[] {
  // Mark the most-recent unmatched started_task as IN PROGRESS. Best-effort
  // pairing — once `task_completed` carries `task_id` we can pair properly.
  const sorted = [...events].sort(
    (a, b) => parseUtc(b.created_at).getTime() - parseUtc(a.created_at).getTime(),
  );
  let inProgressId: number | null = null;
  for (const e of sorted) {
    if (e.extra_info.type === 'started_task') {
      inProgressId = e.id;
      break;
    }
    if (e.extra_info.type === 'task_completed') break;
  }

  const rows: ActivityRow[] = [];
  for (const e of sorted) {
    const time = parseUtc(e.created_at);
    const actor = e.user?.email ? e.user.email.split('@')[0] : 'Verne Operator';
    const info = e.extra_info;
    switch (info.type) {
      case 'dispatched_robot': {
        const detail = formatTravel(info.travel_time_seconds);
        rows.push({
          id: e.id,
          kind: 'dispatch',
          actor,
          action: 'dispatched robot to',
          target: info.destination,
          detail: detail ?? '—',
          time,
          status: 'DONE',
          tone: 'good',
        });
        break;
      }
      case 'started_task': {
        const isInProgress = e.id === inProgressId;
        rows.push({
          id: e.id,
          kind: 'task',
          actor,
          action: 'started task',
          target: null,
          detail: isInProgress ? 'Task in progress' : 'Task started',
          time,
          status: isInProgress ? 'IN PROGRESS' : 'DONE',
          tone: 'good',
        });
        break;
      }
      case 'task_completed': {
        const m = Math.floor(info.cycle_time_seconds / 60);
        const s = info.cycle_time_seconds % 60;
        const detail = m > 0 ? `Cycle time ${m}m ${s.toString().padStart(2, '0')}s` : `Cycle time ${s}s`;
        rows.push({
          id: e.id,
          kind: 'task',
          actor,
          action: 'completed task',
          target: null,
          detail,
          time,
          status: 'DONE',
          tone: 'good',
        });
        break;
      }
      case 'e_stop_pressed': {
        const physical = info.e_stop_type === 'physical';
        rows.push({
          id: e.id,
          kind: 'estop',
          actor: physical ? 'Physical E-Stop' : actor,
          action: physical ? 'of robot' : 'software E-Stop of robot',
          target: null,
          detail: physical ? 'Hardware trigger' : 'Manual via console',
          time,
          status: 'DONE',
          tone: 'bad',
        });
        break;
      }
      case 'started_driving': {
        rows.push({
          id: e.id,
          kind: 'takeover',
          actor,
          action: 'took over driving control',
          target: null,
          detail: 'Manual control',
          time,
          status: 'DONE',
          tone: 'warn',
        });
        break;
      }
    }
  }
  return rows;
}

export function formatTimestamp(d: Date, now: Date = new Date()): string {
  const dKey = pacificDateKey(d);
  const time = d.toLocaleTimeString([], {
    hour: 'numeric', minute: '2-digit', second: '2-digit', timeZone: PACIFIC_TZ,
  });
  if (dKey === pacificDateKey(now)) return `Today, ${time}`;
  if (dKey === pacificDateKey(new Date(now.getTime() - 24 * 60 * 60 * 1000))) return `Yesterday, ${time}`;
  const date = d.toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: PACIFIC_TZ });
  return `${date}, ${time}`;
}

export function formatSeconds(s: number | null): string | null {
  if (s == null) return null;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r > 0 ? `${m}m ${r.toString().padStart(2, '0')}s` : `${m}m`;
}

export function formatMeters(m: number | null): string | null {
  if (m == null) return null;
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

// Monday-anchored start-of-week for the Pacific calendar week containing `d`.
// Returned as a UTC-midnight Date so its day-number matches pacificDayNumber()
// (used as the bucket origin in byDayTaskCounts).
export function startOfWeek(d: Date): Date {
  const [y, m, day] = pacificDateKey(d).split('-').map(Number);
  const out = new Date(Date.UTC(y, m - 1, day));
  const dow = (out.getUTCDay() + 6) % 7; // Mon=0
  out.setUTCDate(out.getUTCDate() - dow);
  return out;
}

/** Pacific day-of-week index (Mon=0 … Sun=6) for an instant. */
export function pacificWeekdayIndex(d: Date): number {
  return pacificDayNumber(d) - startOfWeek(d).getTime() / 86_400_000;
}
